/**
 * Author: Valeria Sinnati
 * |\---/|
 * | ,_, |   Area account del consumatore (M19), su account.<dominio>.
 *  \_`_/-..
 *
 * Sta su un dominio unico e non sotto `/r/{slug}`: lo storico attraversa i
 * ristoranti, appenderlo allo slug di uno solo racconterebbe la cosa sbagliata.
 * La sessione arriva dal cookie di refresh, che vale su `app.` e `account.`:
 * chi accede al tavolo si ritrova dentro anche qui.
 *
 * Ogni ristoratore vede solo i propri dati; qui, e solo qui, il cliente vede
 * i suoi tutti insieme.
 *
 *  /\____/\
 * > •    • <   #1
 */
'use client'

import { ThemeToggle } from '@risto/ui'
import { useEffect, useState } from 'react'
import {
  type ClienteMe,
  esci,
  mieiOrdini,
  mioProfilo,
  type OrdineCliente,
  rinnova,
} from '../../lib/sessione-cliente'

const STATI: Record<string, string> = {
  nuovo: 'in cucina',
  preso_in_carico: 'in preparazione',
  completato: 'servito',
  annullato: 'annullato',
}

function euro(centesimi: number): string {
  return (centesimi / 100).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

export default function AccountPage() {
  const [stato, setStato] = useState<'caricamento' | 'dentro' | 'fuori'>('caricamento')
  const [profilo, setProfilo] = useState<ClienteMe | null>(null)
  const [ordini, setOrdini] = useState<OrdineCliente[]>([])

  useEffect(() => {
    let vivo = true
    ;(async () => {
      // Il token vive in memoria: dopo un ricaricamento si riparte dal cookie
      await rinnova()
      const me = await mioProfilo()
      if (!vivo) return
      if (!me) {
        setStato('fuori')
        return
      }
      setProfilo(me)
      setOrdini(await mieiOrdini())
      setStato('dentro')
    })()
    return () => {
      vivo = false
    }
  }, [])

  if (stato === 'caricamento') {
    return <p className="p-8 text-center text-sm text-muted">Un attimo…</p>
  }

  if (stato === 'fuori') {
    return (
      <main className="mx-auto max-w-md p-6">
        <h1 className="text-[1.375rem] text-foreground">Il tuo profilo</h1>
        <p className="mt-3 text-sm text-muted">
          Qui trovi gli ordini fatti nei ristoranti dove hai usato il menu al tavolo. Per entrare,
          inquadra il QR del tavolo e accedi dopo aver ordinato: ti mandiamo un codice via SMS.
        </p>
      </main>
    )
  }

  // Un raggruppamento per locale renderebbe la pagina più ordinata ma
  // nasconderebbe la cosa che si cerca davvero: l'ultima volta
  return (
    <main className="mx-auto max-w-2xl p-4 sm:p-6">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[1.375rem] text-foreground">
            {profilo?.nome ? `Ciao ${profilo.nome}` : 'Il tuo profilo'}
          </h1>
          <p className="mt-1 text-sm text-muted">{profilo?.phone}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ThemeToggle />
          <button
            type="button"
            onClick={async () => {
              await esci()
              setStato('fuori')
            }}
            className="min-h-11 shrink-0 rounded-pill bg-soft px-5 text-sm font-bold text-foreground"
          >
            Esci
          </button>
        </div>
      </header>

      <h2 className="mb-3 font-body text-xs font-bold tracking-[0.12em] text-muted uppercase">
        I tuoi ordini
      </h2>
      <ul className="flex flex-col gap-3">
        {ordini.map((o) => (
          <li key={o.id} className="rounded-lg border-[1.5px] border-line bg-panel p-4">
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <b className="truncate text-foreground">{o.ristorante}</b>
              <span className="shrink-0 text-xs text-muted">
                {new Date(o.created_at).toLocaleDateString('it-IT', {
                  day: '2-digit',
                  month: 'long',
                })}
              </span>
            </div>
            <ul className="mb-2 flex flex-col gap-0.5">
              {o.items.map((i) => (
                <li key={`${o.id}-${i.nome}`} className="flex justify-between text-sm">
                  <span className="text-muted">
                    {i.qty}× {i.nome}
                  </span>
                  <span className="text-muted">{euro(i.prezzo_unitario * i.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between border-t border-line pt-2">
              <span className="rounded-pill bg-soft px-2.5 py-0.5 text-xs font-semibold text-foreground">
                {STATI[o.stato] ?? o.stato}
              </span>
              <b className="font-heading text-base text-foreground">{euro(o.totale)}</b>
            </div>
          </li>
        ))}
        {!ordini.length ? (
          <li className="rounded-lg bg-soft p-8 text-center text-sm text-muted">
            Nessun ordine, per ora.
          </li>
        ) : null}
      </ul>
    </main>
  )
}
